import { openDB, IDBPDatabase } from "idb";

// Define the data interfaces
interface DataItem {
    id: string;
    content: unknown;
    timestamp: number;
    synced: boolean;
}

interface SettingItem {
    key: string;
    value: unknown;
}

interface MediaItem {
    id: string;
    url: string;
    filename: string;
    type: "image" | "video";
    size: number;
    mimeType: string;
    downloadedAt: number;
    localPath: string; // Path to file in public/media directory
    blobUrl?: string; // Temporary blob URL for display
}

// Define the database schema
interface AppDB {
    data: DataItem;
    settings: SettingItem;
    media: MediaItem;
}

class IndexedDBService {
    private db: IDBPDatabase<AppDB> | null = null;
    private readonly dbName = "DST-Bravilor-PWA";
    private readonly dbVersion = 2;

    async init(): Promise<void> {
        try {
            this.db = await openDB<AppDB>(this.dbName, this.dbVersion, {
                upgrade(db, oldVersion) {
                    // Create object stores
                    if (!db.objectStoreNames.contains("data")) {
                        const dataStore = db.createObjectStore("data", {
                            keyPath: "id",
                        });
                        dataStore.createIndex("timestamp", "timestamp");
                        dataStore.createIndex("synced", "synced");
                    }

                    if (!db.objectStoreNames.contains("settings")) {
                        db.createObjectStore("settings", { keyPath: "key" });
                    }

                    // Create media store (new in version 2)
                    if (
                        oldVersion < 2 &&
                        !db.objectStoreNames.contains("media")
                    ) {
                        const mediaStore = db.createObjectStore("media", {
                            keyPath: "id",
                        });
                        mediaStore.createIndex("type", "type");
                        mediaStore.createIndex("downloadedAt", "downloadedAt");
                        mediaStore.createIndex("url", "url");
                    }
                },
            });
            console.log("IndexedDB initialized successfully");
        } catch (error) {
            console.error("Failed to initialize IndexedDB:", error);
            throw error;
        }
    }

    async saveData(
        id: string,
        content: unknown,
        synced: boolean = false,
    ): Promise<void> {
        if (!this.db) {
            await this.init();
        }

        try {
            await this.db!.put("data", {
                id,
                content,
                timestamp: Date.now(),
                synced,
            });
            console.log(`Data saved to IndexedDB: ${id}`);
        } catch (error) {
            console.error("Failed to save data to IndexedDB:", error);
            throw error;
        }
    }

    async getAllData(): Promise<DataItem[]> {
        if (!this.db) {
            await this.init();
        }

        try {
            const results = await this.db!.getAll("data");
            return results.map((item) => ({
                id: item.id,
                content: item.content,
                timestamp: item.timestamp,
                synced: item.synced,
            }));
        } catch (error) {
            console.error("Failed to get all data from IndexedDB:", error);
            return [];
        }
    }

    async saveSetting(key: string, value: unknown): Promise<void> {
        if (!this.db) {
            await this.init();
        }

        try {
            await this.db!.put("settings", { key, value });
            console.log(`Setting saved: ${key}`);
        } catch (error) {
            console.error("Failed to save setting:", error);
            throw error;
        }
    }

    async getSetting(key: string): Promise<unknown | null> {
        if (!this.db) {
            await this.init();
        }

        try {
            const result = await this.db!.get("settings", key);
            return result ? result.value : null;
        } catch (error) {
            console.error("Failed to get setting:", error);
            return null;
        }
    }

    // Media-specific methods
    async saveMediaItem(mediaItem: MediaItem): Promise<void> {
        if (!this.db) await this.init();

        try {
            await this.db!.put("media", mediaItem);
            console.log(`Media saved: ${mediaItem.filename}`);
        } catch (error) {
            console.error("Failed to save media:", error);
            throw error;
        }
    }

    async getAllMediaItems(): Promise<MediaItem[]> {
        if (!this.db) await this.init();

        try {
            return await this.db!.getAll("media");
        } catch (error) {
            console.error("Failed to get all media:", error);
            return [];
        }
    }

    async getMediaByUrl(url: string): Promise<MediaItem | null> {
        if (!this.db) await this.init();

        try {
            const results = await this.db!.getAllFromIndex("media", "url", url);
            return results.length > 0 ? results[0] : null;
        } catch (error) {
            console.error("Failed to get media by URL:", error);
            return null;
        }
    }
}

// Export a singleton instance
export const indexedDBService = new IndexedDBService();
